import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ChatActions from './chat.actions';
import {
  ConnectionState,
  IChatAuth,
  ICustomerUser
} from '@care-portals/api-interfaces';
import { Participant } from '@twilio/conversations';

export interface ChatEntity {
  id: string;
  messages: any[];
  participants: Participant[];
  customerId: string;
}
export const CHAT_FEATURE_KEY = 'chat';

export interface State extends EntityState<ChatEntity> {
  selectedId: string; // which Chat record has been selected
  selectedChatCount: number;
  isInitialized: boolean;
  isReady: boolean;
  connectionState: ConnectionState;
  statusString: string;
  loading: boolean; //  if the appiontment list is loading
  audioId: string;
  videoId: string;
  error?: string | null; // last none error (if any)
  auth: IChatAuth;
  identity: string;
  isSidebarOpen: boolean;
  activeCustomer: ICustomerUser;
  mode: 'chat' | 'inbox';
  /**
   * Bumped on every openChat. Identifies the current chat session so a
   * teardown issued by a superseded view can be told apart from one issued
   * by the view that is actually on screen.
   */
  sessionId: number;
}

export function selectChatId(chat: ChatEntity): string {
  return chat.id;
}

export const chatAdapter: EntityAdapter<ChatEntity> =
  createEntityAdapter<ChatEntity>({
    selectId: selectChatId
  });

export const initialState: State = chatAdapter.getInitialState({
  // set initial required properties
  selectedId: null,
  selectedChatCount: 0,
  isInitialized: false,
  isReady: false,
  ready: false,
  connectionState: 'disconnected',
  statusString: 'Not Connected',
  loading: false,
  conversation: null,
  audioId: null,
  videoId: null,
  auth: null,
  identity: null,
  remoteTracks: {},
  isSidebarOpen: false,
  activeCustomer: null,
  mode: 'inbox',
  sessionId: 0
});

const chatReducer = createReducer(
  initialState,
  on(ChatActions.openChat, (state, { customer, mode, style }) => ({
    ...state,
    loading: true,
    isSidebarOpen: style === 'sidebar' ? true : false,
    mode: mode || 'inbox',
    activeCustomer: customer,
    sessionId: state.sessionId + 1
  })),
  on(ChatActions.startChat, state => {
    return chatAdapter.removeAll({
      ...state,
      auth: null,
      identity: null,
      isInitialized: false,
      isReady: false,
      statusString: 'Starting Session...',
      selectedId: null
    });
  }),
  on(ChatActions.startChatSuccess, (state, { customer, response }) =>
    chatAdapter.upsertOne(
      {
        id: response.chatId,
        messages: [],
        participants: [],
        customerId: customer._id
      },
      {
        ...state,
        auth: response.auth,
        identity: response.identity,
        loading: false,
        selectedId: response.chatId,
        activeCustomer: customer
      }
    )
  ),
  on(ChatActions.refreshTokenSuccess, (state, { auth }) => {
    return {
      ...state,
      auth
    };
  }),
  on(ChatActions.connectionStateChanged, (state, { connectionState }) => {
    if (connectionState === 'connecting') {
      return {
        ...state,
        statusString: 'Connecting...',
        connectionState: connectionState
      };
    } else if (connectionState === 'connected') {
      return {
        ...state,
        statusString: 'You are connected',
        connectionState: connectionState
      };
    } else if (connectionState === 'disconnecting') {
      return {
        ...state,
        statusString: 'Disconnecting...',
        isReady: false,
        connectionState: connectionState
      };
    } else if (connectionState === 'disconnected') {
      return {
        ...state,
        statusString: 'Disconnected',
        isReady: false,
        connectionState: connectionState
      };
    } else if (connectionState === 'denied') {
      return {
        ...state,
        statusString: 'Failed to connect',
        isReady: false,
        connectionState: connectionState
      };
    } else {
      return {
        ...state,
        connectionState: connectionState
      };
    }
  }),
  on(ChatActions.loadMessages, state => {
    return { ...state, loading: true };
  }),
  on(ChatActions.loadMessagesSuccess, (state, { messages }) => {
    return chatAdapter.updateOne(
      {
        id: state.selectedId,
        changes: {
          messages
        }
      },
      {
        ...state,
        loading: false
      }
    );
  }),
  on(ChatActions.loadMessagesFailure, (state, { error }) => {
    return {
      ...state,
      loading: false
    };
  }),
  on(ChatActions.updateChatCount, (state, { chatId, count }) => {
    return { ...state, selectedChatCount: count };
  }),
  on(ChatActions.loadMore, state => {
    return { ...state, loading: true };
  }),
  on(ChatActions.loadMoreSuccess, (state, { messages, chatId }) => {
    const currentMessages = state.entities[chatId]?.messages || [];
    return chatAdapter.updateOne(
      {
        id: chatId,
        changes: {
          messages: [...messages, ...currentMessages]
        }
      },
      {
        ...state,
        loading: false
      }
    );
  }),
  on(ChatActions.loadMessagesFailure, (state, { error }) => {
    return {
      ...state,
      loading: false
    };
  }),

  on(ChatActions.messageAdded, (state, { chatId, message }) => {
    return chatAdapter.updateOne(
      {
        id: chatId,
        changes: {
          messages: [...state.entities[chatId].messages, message]
        }
      },
      { ...state }
    );
  }),
  on(ChatActions.closeChat, (state, { sessionId }) => {
    // A close tagged with an older session belongs to a view that has already
    // been superseded — its drawer is animating out while a newer chat is on
    // screen. Honouring it would null activeCustomer under the live view,
    // which is what surfaced as "There was an error Loading Customer".
    if (sessionId != null && sessionId !== state.sessionId) {
      return state;
    }
    return chatAdapter.removeAll({
      ...state,
      auth: null,
      identity: null,
      isInitialized: false,
      isReady: false,
      statusString: 'Ending Session...',
      isSidebarOpen: false,
      activeCustomer: null,
      selectedId: null
    });
  })

  // VIDEO REDUCERS
  // on(ChatActions.joinVideoSuccess, (state, { hostLink }) => {
  //   return chatAdapter.updateOne(
  //     {
  //       id: chatId,
  //       changes: {
  //         roomId
  //       }
  //     },
  //     { ...state }
  //   );
  // }),
  // on(ChatActions.joinVideoFailure, (state, { chatId, error }) =>
  //   chatAdapter.updateOne(
  //     {
  //       id: chatId,
  //       changes: {
  //         roomId: null
  //       }
  //     },
  //     { ...state, loading: false }
  //   )
  // ),
);

export function reducer(state: State | undefined, action: Action) {
  return chatReducer(state, action);
}
